import { t } from "@lingui/macro";
import {
  Article,
  Books,
  Brain,
  Briefcase,
  CaretLeft,
  CaretRight,
  Certificate,
  Check,
  GameController,
  GraduationCap,
  HandHeart,
  Medal,
  PaintBrushHousehold,
  PenNib,
  PuzzlePiece,
  ShareNetwork,
  SquaresFour,
  Star,
  Translate,
  Trophy,
  User,
  Users,
} from "@phosphor-icons/react";
import * as PhosphorIcons from "@phosphor-icons/react";
import { Tooltip } from "@stylingcv/ui";
import { useState } from "react";

import { UserProfileFooter } from "@/client/components/user-profile-footer";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { getCurrentUrlLocale } from "@/client/utils/url-locales";
import { isRTLLocale } from "@/client/utils/rtl-helpers";

function kebabToPascal(str: string): string {
  return str
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}

/* ── Section definitions ─────────────────────────────────────────────────── */
/* eslint-disable lingui/no-unlocalized-strings */
const SECTIONS = [
  { id: "basics", label: "Personal Info", Icon: User },
  { id: "summary", label: "Summary", Icon: Article },
  { id: "experience", label: "Experience", Icon: Briefcase },
  { id: "education", label: "Education", Icon: GraduationCap },
  { id: "skills", label: "Skills", Icon: Star },
  { id: "projects", label: "Projects", Icon: PuzzlePiece },
  { id: "languages", label: "Languages", Icon: Translate },
  { id: "certifications", label: "Certifications", Icon: Certificate },
  { id: "interests", label: "Interests", Icon: GameController },
  { id: "hobbies", label: "Hobbies", Icon: GameController },
  { id: "volunteer", label: "Volunteer", Icon: HandHeart },
  { id: "awards", label: "Awards", Icon: Medal },
  { id: "publications", label: "Publications", Icon: Books },
  { id: "references", label: "References", Icon: Users },
  { id: "profiles", label: "Profiles", Icon: ShareNetwork },
  { id: "mostProudOf", label: "Most Proud Of", Icon: Trophy },
  { id: "myMind", label: "My Mind", Icon: Brain },
  { id: "signature", label: "Signature", Icon: PenNib },
] as const;
/* eslint-enable lingui/no-unlocalized-strings */

/* ── Badge helper ─────────────────────────────────────────────────────────── */
type BadgeInfo = { type: "check" } | { type: "count"; value: number } | { type: "add" };

function getSectionBadge(sectionId: string, resumeData: any): BadgeInfo {
  if (sectionId === "basics") {
    const b = resumeData?.basics;
    return b?.name && b?.email ? { type: "check" } : { type: "add" };
  }
  if (sectionId === "summary") {
    return resumeData?.summary?.content ? { type: "check" } : { type: "add" };
  }
  const items = resumeData?.sections?.[sectionId]?.items ?? [];
  if (items.length === 0) return { type: "add" };
  return { type: "count", value: items.length };
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export const SectionRail = () => {
  const activeSectionId = useBuilderStore((s) => s.activeSectionId);
  const setActiveSectionId = useBuilderStore((s) => s.setActiveSectionId);
  const leftPanelMode = useBuilderStore((s) => s.leftPanelMode);
  const setLeftPanelMode = useBuilderStore((s) => s.setLeftPanelMode);
  const setActiveTab = useBuilderStore((s) => s.setActiveTab);
  const resumeData = useResumeStore((s) => s.resume?.data);
  const isCoverLetter = useResumeStore((s) => (s.resume as any)?.type === "coverLetter");
  const customSections = useResumeStore((s) => {
    try {
      return s.resume.data.sections.custom || {};
    } catch {
      return {} as Record<string, { name: string }>;
    }
  });
  const addSection = useResumeStore((s) => s.addSection);
  // Read per-section custom icons so rail stays in sync with middle editor
  const sectionCustomIcons = useResumeStore((s) => {
    try {
      const sections = s.resume.data.sections as Record<string, { icon?: string }>;
      return Object.fromEntries(Object.entries(sections).map(([k, v]) => [k, v?.icon])) as Record<
        string,
        string | undefined
      >;
    } catch {
      return {} as Record<string, string | undefined>;
    }
  });
  // Read per-section live names so renames in the middle editor reflect immediately
  const sectionNames = useResumeStore((s) => {
    try {
      const sections = s.resume.data.sections as Record<string, { name?: string }>;
      return Object.fromEntries(Object.entries(sections).map(([k, v]) => [k, v?.name])) as Record<
        string,
        string | undefined
      >;
    } catch {
      return {} as Record<string, string | undefined>;
    }
  });
  // Read per-section visibility so hidden sections appear faded in the rail
  const sectionVisibility = useResumeStore((s) => {
    try {
      const sections = s.resume.data.sections as Record<string, { visible?: boolean }>;
      return Object.fromEntries(
        Object.entries(sections).map(([k, v]) => [k, v?.visible !== false]),
      ) as Record<string, boolean>;
    } catch {
      return {} as Record<string, boolean>;
    }
  });
  const [collapsed, setCollapsed] = useState(false);
  const [handleHovered, setHandleHovered] = useState(false);

  const currentLocale = getCurrentUrlLocale();
  const isRTL = isRTLLocale(currentLocale);
  const isDesignActive = leftPanelMode === "design";

  const resumeCount = useResumeStore((s) => {
    try {
      return Object.keys(s.resume?.data?.sections ?? {}).length;
    } catch {
      return 0;
    }
  });

  return (
    <aside
      className="stylion-app-shell relative flex h-full flex-col border-r"
      style={{
        width: collapsed ? 52 : 248,
        minWidth: collapsed ? 52 : 248,
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflow: "visible",
        zIndex: 30,
      }}
    >
      {/* ── Collapse handle — replaces the aside border ── */}
      <Tooltip content={collapsed ? t`Expand sidebar` : t`Collapse sidebar`} side={isRTL ? "left" : "right"}>
        <button
          aria-label={collapsed ? t`Expand sidebar` : t`Collapse sidebar`}
          style={{
            position: "absolute",
            ...(isRTL ? { left: -10 } : { right: -10 }),
            top: 0,
            bottom: 0,
            width: 20,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 20,
            background: "transparent",
            border: "none",
            padding: 0,
            gap: 0,
          }}
          onClick={() => {
            setCollapsed((v) => !v);
          }}
          onMouseEnter={() => {
            setHandleHovered(true);
          }}
          onMouseLeave={() => {
            setHandleHovered(false);
          }}
        >
          {/* Top portion of the line */}
          <div
            style={{
              flex: 1,
              width: 2,
              borderRadius: "2px 2px 0 0",
              background: handleHovered ? "var(--accent,#268599)" : "var(--border,#e5e5e5)",
              transition: "background 0.15s",
            }}
          />

          {/* Arrow badge — centred on the line */}
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: handleHovered ? "var(--accent,#268599)" : "#fff",
              border: `1.5px solid ${handleHovered ? "var(--accent,#268599)" : "var(--border,#e5e5e5)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
              zIndex: 1,
            }}
          >
            {collapsed ? (
              isRTL ? (
                <CaretLeft
                  size={8}
                  weight="bold"
                  style={{ color: handleHovered ? "#fff" : "var(--fg-3,#737373)", flexShrink: 0 }}
                />
              ) : (
                <CaretRight
                  size={8}
                  weight="bold"
                  style={{ color: handleHovered ? "#fff" : "var(--fg-3,#737373)", flexShrink: 0 }}
                />
              )
            ) : (
              isRTL ? (
                <CaretRight
                  size={8}
                  weight="bold"
                  style={{ color: handleHovered ? "#fff" : "var(--fg-3,#737373)", flexShrink: 0 }}
                />
              ) : (
                <CaretLeft
                  size={8}
                  weight="bold"
                  style={{ color: handleHovered ? "#fff" : "var(--fg-3,#737373)", flexShrink: 0 }}
                />
              )
            )}
          </div>

          {/* Bottom portion of the line */}
          <div
            style={{
              flex: 1,
              width: 2,
              borderRadius: "0 0 2px 2px",
              background: handleHovered ? "var(--accent,#268599)" : "var(--border,#e5e5e5)",
              transition: "background 0.15s",
            }}
          />
        </button>
      </Tooltip>
      {/* ── Scrollable section list ── */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ padding: "20px 12px 16px" }}
      >
        {/* Cover letter mode — hide all resume sections */}
        {isCoverLetter && !collapsed && (
          <div
            style={{
              padding: "8px 12px",
              fontSize: 11,
              fontWeight: 600,
              color: "var(--fg-4,#a3a3a3)",
              textAlign: "center",
              lineHeight: 1.4,
            }}
          >
            {t`Cover Letter`}
          </div>
        )}

        {/* Label row */}
        {!isCoverLetter && !collapsed && (
          <button
            className="mb-3 flex w-full items-center"
            style={{
              gap: 12,
              padding: "10px 12px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: isDesignActive ? 700 : 600,
              color: isDesignActive ? "var(--accent,#268599)" : "var(--fg-2,#404040)",
              background: isDesignActive ? "rgba(38,133,153,0.10)" : "var(--scv-gray-100,#f5f5f5)",
              border: "1px solid rgba(38,133,153,0.10)",
              cursor: "pointer",
              textAlign: "left",
              transition: "background 0.15s, color 0.15s",
              position: "relative",
            }}
            onClick={() => {
              setActiveTab("design");
              setLeftPanelMode("design");
            }}
            onMouseEnter={(e) => {
              if (!isDesignActive) {
                (e.currentTarget as HTMLElement).style.background = "rgba(38,133,153,0.08)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isDesignActive) {
                (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#f5f5f5)";
              }
            }}
          >
            {isDesignActive && (
              <span
                style={{
                  position: "absolute",
                  ...(isRTL ? { right: -3 } : { left: -3 }),
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: isRTL ? "3px 0 0 3px" : "0 3px 3px 0",
                  background: "var(--accent,#268599)",
                }}
              />
            )}
            <PaintBrushHousehold size={17} weight={isDesignActive ? "fill" : "duotone"} />
            <span style={{ flex: 1 }}>{t`Change Design`}</span>
          </button>
        )}

        {!isCoverLetter && collapsed && (
          <Tooltip content={t`Change Design`} side={isRTL ? "left" : "right"}>
            <button
              className="mb-3 flex w-full items-center justify-center"
              style={{
                padding: "9px 0",
                borderRadius: 8,
                color: isDesignActive ? "var(--accent,#268599)" : "var(--fg-2,#404040)",
                background: isDesignActive ? "rgba(38,133,153,0.10)" : "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background 0.15s, color 0.15s",
              }}
              onClick={() => {
                setActiveTab("design");
                setLeftPanelMode("design");
              }}
              onMouseEnter={(e) => {
                if (!isDesignActive) {
                  (e.currentTarget as HTMLElement).style.background = "var(--scv-gray-100,#f5f5f5)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isDesignActive) {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                }
              }}
            >
              <PaintBrushHousehold size={17} weight={isDesignActive ? "fill" : "duotone"} />
            </button>
          </Tooltip>
        )}

        {!isCoverLetter && !collapsed && (
          <div
            className="mb-2 flex items-center justify-between"
            style={{
              padding: "0 12px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-4,#a3a3a3)",
            }}
          >
            <span>{t`Sections`}</span>
            <button
              style={{
                fontSize: 14,
                color: "var(--fg-3,#737373)",
                lineHeight: 1,
                padding: "0 4px",
                cursor: "pointer",
                background: "none",
                border: "none",
              }}
              onClick={() => {
                try {
                  addSection();
                } catch {
                  /**/
                }
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--accent,#268599)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = "var(--fg-3,#737373)";
              }}
            >
              +
            </button>
          </div>
        )}

        {/* Section items — hidden for cover letters */}
        <div className="mb-6" style={{ display: isCoverLetter ? "none" : undefined }}>
          {SECTIONS.map(({ id, label, Icon }) => {
            const badge = getSectionBadge(id, resumeData);
            const isActive = !isDesignActive && activeSectionId === id;
            const customIcon = sectionCustomIcons[id];
            const isHidden = sectionVisibility[id] === false;
            return (
              <Tooltip
                key={id}
                content={collapsed ? sectionNames[id] || label : undefined}
                side={isRTL ? "left" : "right"}
              >
                <button
                  className="group relative flex w-full items-center"
                  style={{
                    gap: collapsed ? 0 : 12,
                    padding: collapsed ? "9px 0" : "9px 12px",
                    justifyContent: collapsed ? "center" : undefined,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "var(--accent,#268599)" : "var(--fg-2,#404040)",
                    background: isActive ? "rgba(38,133,153,0.10)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.15s, opacity 0.15s",
                    position: "relative",
                    opacity: isHidden ? 0.38 : 1,
                  }}
                  onClick={() => {
                    setActiveSectionId(id);
                    if (leftPanelMode === "design") {
                      setLeftPanelMode("content");
                      setActiveTab("fill-in");
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLElement).style.background =
                        "var(--scv-gray-100,#f5f5f5)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLElement).style.background = "transparent";
                  }}
                >
                  {/* Active edge bar */}
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        ...(isRTL ? { right: -3 } : { left: -3 }),
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: isRTL ? "3px 0 0 3px" : "0 3px 3px 0",
                        background: "var(--accent,#268599)",
                      }}
                    />
                  )}

                  {/* Icon — custom icon from resume data syncs with middle editor */}
                  {customIcon ? (
                    (() => {
                      const DynIcon = (PhosphorIcons as Record<string, any>)[
                        kebabToPascal(customIcon)
                      ];
                      return DynIcon ? (
                        <DynIcon
                          size={17}
                          weight="regular"
                          style={{ flexShrink: 0, opacity: 0.85 }}
                        />
                      ) : (
                        <Icon size={17} weight="regular" style={{ flexShrink: 0, opacity: 0.85 }} />
                      );
                    })()
                  ) : (
                    <Icon size={17} weight="regular" style={{ flexShrink: 0, opacity: 0.85 }} />
                  )}

                  {/* Label — live from store, falls back to hardcoded default */}
                  {!collapsed && <span style={{ flex: 1 }}>{sectionNames[id] || label}</span>}

                  {/* Badge */}
                  {!collapsed && badge.type === "check" && (
                    <Check
                      size={14}
                      weight="bold"
                      style={{
                        marginLeft: "auto",
                        flexShrink: 0,
                        color: "var(--scv-success,#22c55e)",
                      }}
                    />
                  )}
                  {!collapsed && badge.type === "count" && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        color: isActive ? "var(--accent,#268599)" : "var(--fg-3,#737373)",
                        background: isActive
                          ? "rgba(38,133,153,0.15)"
                          : "var(--scv-gray-100,#f5f5f5)",
                        padding: "2px 7px",
                        borderRadius: 9999,
                        flexShrink: 0,
                      }}
                    >
                      {badge.value}
                    </span>
                  )}
                  {!collapsed && badge.type === "add" && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        color: "var(--scv-coral-deep,#cd7858)",
                        background: "rgba(232,136,94,0.10)",
                        padding: "2px 7px",
                        borderRadius: 9999,
                        flexShrink: 0,
                      }}
                    >
                      add
                    </span>
                  )}
                </button>
              </Tooltip>
            );
          })}

          {/* Custom sections — hidden for cover letters via parent display:none */}
          {!isCoverLetter && Object.entries(customSections).map(([id, { name }]) => {
            const isActive = !isDesignActive && activeSectionId === `custom.${id}`;
            const isCustomHidden = sectionVisibility[`custom.${id}`] === false;
            return (
              <Tooltip key={id} content={collapsed ? name : undefined} side={isRTL ? "left" : "right"}>
                <button
                  className="relative flex w-full items-center"
                  style={{
                    gap: collapsed ? 0 : 12,
                    padding: collapsed ? "9px 0" : "9px 12px",
                    justifyContent: collapsed ? "center" : undefined,
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? "var(--accent,#268599)" : "var(--fg-2,#404040)",
                    background: isActive ? "rgba(38,133,153,0.10)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.15s, opacity 0.15s",
                    opacity: isCustomHidden ? 0.38 : 1,
                  }}
                  onClick={() => {
                    setActiveSectionId(`custom.${id}`);
                    if (leftPanelMode === "design") {
                      setLeftPanelMode("content");
                      setActiveTab("fill-in");
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLElement).style.background =
                        "var(--scv-gray-100,#f5f5f5)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLElement).style.background = "transparent";
                  }}
                >
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        ...(isRTL ? { right: -3 } : { left: -3 }),
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: isRTL ? "3px 0 0 3px" : "0 3px 3px 0",
                        background: "var(--accent,#268599)",
                      }}
                    />
                  )}
                  <SquaresFour
                    size={17}
                    weight="regular"
                    style={{ flexShrink: 0, opacity: 0.85 }}
                  />
                  {!collapsed && <span style={{ flex: 1 }}>{name}</span>}
                </button>
              </Tooltip>
            );
          })}
        </div>

        {/* ── Pro Tip card ── */}
        {!collapsed && (
          <div
            style={{
              margin: "0 8px",
              padding: 16,
              borderRadius: 12,
              background:
                "var(--gradient-navy-fade, linear-gradient(180deg,#0a1628 0%,#16344c 50%,#1a3a4f 100%))",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Coral glow overlay */}
            <div
              style={{
                position: "absolute",
                right: -20,
                top: -20,
                width: 90,
                height: 90,
                background: "radial-gradient(circle, rgba(232,136,94,0.40), transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <p
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--scv-coral-warm,#daa579)",
                marginBottom: 6,
              }}
            >
              {t`Pro Tip`}
            </p>
            <p
              style={{
                fontFamily: "var(--font-serif,Georgia,serif)",
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.25,
                margin: "0 0 4px",
                color: "#fff",
              }}
            >
              {t`Lead bullets with a verb & a number.`}
            </p>
            <p style={{ fontSize: 11, lineHeight: 1.5, opacity: 0.75, margin: 0, color: "#fff" }}>
              {t`Resumes that quantify outcomes get 27% more recruiter callbacks in MENA markets.`}
            </p>
            <a
              href="#"
              style={{
                marginTop: 10,
                display: "inline-flex",
                gap: 4,
                alignItems: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--scv-coral-warm,#daa579)",
                textDecoration: "none",
              }}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {t`See examples →`}
            </a>
          </div>
        )}
      </div>

      {/* ── User menu (pinned bottom) ── */}
      <UserProfileFooter collapsed={collapsed} />
    </aside>
  );
};
